<template>
  <div class="display-mode pb-10">
    <div class="dm-section">
      <p class="dm-section__title">Carpets</p>
      <p class="dm-section__description">You can add multiple carpets to an event.</p>
      <div
        v-for="(carpet, index) in eventCarpets"
        :key="JSON.stringify(carpet)"
        class="carpet-item"
      >
        <v-text-field
          :value="carpet.name"
          autocomplete="off"
          background-color="#fff"
          class="carpet__field"
          label="Title"
          outlined
          hide-details
          @input="handleUpdateCarpetName(index, carpet.name)"
        />
        <v-btn
          class="carpet__remove-btn"
          color="red"
          :disabled="isUpdatingCarpets"
          icon
          text
          @click="handleRemoveCarpet(index)"
        >
          <v-icon>{{ mdiMinusCircleOutline }}</v-icon>
        </v-btn>
      </div>
      <v-btn
        class="carpet__add-btn pa-0"
        color="var(--color-blue-800)"
        :disabled="isUpdatingCarpets"
        text
        tile
        @click="handleAddCarpet"
      >
        Add carpet
        <v-icon>{{ mdiPlus }}</v-icon>
      </v-btn>
    </div>

    <div class="dm-section">
      <p class="dm-section__title">Display Mode Logo</p>
      <p class="dm-section__description">
        You can add a logo to the display mode.
        <br />
        The recommended aspect ratio is 1:1. For example, 800x800px.
        <br />
        Recommended format is PNG or WEBP with transparent background.
      </p>
      <v-img
        :src="srcLogo"
        class="dm-logo"
        :style="{ opacity: isSavingLogo ? 0.5 : 1 }"
      >
        <v-btn
          v-if="!srcLogo || isSavingLogo"
          :loading="isSavingLogo"
          class="btn-upload"
          color="var(--color-white)"
          outlined
          tile
          @click="openLogoDialog"
        >
          Upload Logo
          <v-icon class="ml-4">
            {{ mdiUpload }}
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          class="btn-edit white--text"
          color="var(--color-blue-800)"
          tile
          @click="openLogoDialog"
        >
          Edit Logo
          <v-icon class="ml-4">
            {{ mdiLeadPencil }}
          </v-icon>
        </v-btn>
        <!--      eslint-disable vuejs-accessibility/form-control-has-label-->
        <input
          ref="inputLogoRef"
          accept="image/png, image/jpeg, image/webp"
          name="DMLogo"
          style="display: none"
          type="file"
          @change="handlerLogoChange"
        />
        <!--      eslint-enable vuejs-accessibility/form-control-has-label-->
      </v-img>

      <p
        v-if="logoError"
        class="error--text mt-2"
      >
        {{ logoError }}
      </p>
    </div>

    <div class="dm-section">
      <p class="dm-section__title">Display Mode Background Image</p>
      <p class="dm-section__description">
        You can add a background image to the display mode.
        <br />
        The image will be cropped to fit the display mode.
        <br />
        The recommended aspect ratio is 16:9. For example, 1920x1080px.
      </p>
      <v-img
        :src="srcScoreboardBgImage"
        class="dm-bg-image"
        :style="{ opacity: isSavingBgImage ? 0.5 : 1 }"
      >
        <v-btn
          v-if="!srcScoreboardBgImage || isSavingBgImage"
          :loading="isSavingBgImage"
          class="btn-upload"
          color="var(--color-white)"
          outlined
          tile
          @click="openBgImageDialog"
        >
          Upload image
          <v-icon class="ml-4">
            {{ mdiUpload }}
          </v-icon>
        </v-btn>
        <v-btn
          v-else
          class="btn-edit white--text"
          color="var(--color-blue-800)"
          tile
          @click="openBgImageDialog"
        >
          Edit Image
          <v-icon class="ml-4">
            {{ mdiLeadPencil }}
          </v-icon>
        </v-btn>
        <!--      eslint-disable vuejs-accessibility/form-control-has-label-->
        <input
          ref="inputBgImageRef"
          accept="image/png, image/jpeg"
          name="DMBgImage"
          style="display: none"
          type="file"
          @change="handlerBgImageChange"
        />
        <!--      eslint-enable vuejs-accessibility/form-control-has-label-->
      </v-img>

      <p
        v-if="bgImageError"
        class="error--text mt-2"
      >
        {{ bgImageError }}
      </p>
    </div>

    <EventEditActionButtons
      :is-loading="isPending || isUpdatingCarpets || isSavingBgImage || isSavingLogo"
      :disabled="!isInfoFilledOut || isUpdatingCarpets || isSavingBgImage || isSavingLogo"
      :error="errorMessage || error?.message"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import { mdiLeadPencil, mdiMinusCircleOutline, mdiPlus, mdiUpload } from '@mdi/js'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { eventApi, IEventEdit } from '@/enitites/event'
import { useEventUpdateMutation } from '@/enitites/event/api/queries'
import { IEventCarpet } from '@/enitites/event/model/types'

const { getImageById, saveImage } = useImage()
const { error, isPending, mutate: updateEvent } = useEventUpdateMutation()

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const scoreboardBgImage = ref(props.eventData.scoreboardBgImage)
const scoreboardLogo = ref(props.eventData.scoreboardLogo)
const inputBgImageRef = ref<HTMLInputElement | null>(null)
const inputLogoRef = ref<HTMLInputElement | null>(null)
const eventCarpets = ref<IEventCarpet[]>(props.eventData.carpets || [])
const isUpdatingCarpets = ref(false)

const errorMessage = ref<string>()
const isSavingBgImage = ref(false)
const isSavingLogo = ref(false)
const bgImageError = ref<string>()
const logoError = ref<string>()
const optimisticBgImage = ref<string>()
const optimisticLogo = ref<string>()

const srcScoreboardBgImage = computed(() => {
  if (optimisticBgImage.value) {
    return optimisticBgImage.value
  }
  return scoreboardBgImage.value ? getImageById(scoreboardBgImage.value, 800, 450) : ''
})

const srcLogo = computed(() => {
  if (optimisticLogo.value) {
    return optimisticLogo.value
  }
  return scoreboardLogo.value ? getImageById(scoreboardLogo.value, 800, 800) : ''
})

const isInfoFilledOut = computed(() => !!props.eventData.title)

async function saveBgImageToEvent(imageFile: File): Promise<void> {
  isSavingBgImage.value = true
  bgImageError.value = undefined

  try {
    const result = await saveImage(imageFile)
    if (result) {
      scoreboardBgImage.value = result
    } else {
      optimisticBgImage.value = undefined
      bgImageError.value = 'Failed to upload background image'
    }
  } catch (e) {
    optimisticBgImage.value = undefined
    bgImageError.value = (e as Error).message || 'Failed to upload background image'
  } finally {
    isSavingBgImage.value = false
  }
}

async function saveLogoToEvent(imageFile: File): Promise<void> {
  isSavingLogo.value = true
  logoError.value = undefined

  try {
    const result = await saveImage(imageFile)
    if (result) {
      scoreboardLogo.value = result
    } else {
      optimisticLogo.value = undefined
      logoError.value = 'Failed to upload logo'
    }
  } catch (e) {
    optimisticLogo.value = undefined
    logoError.value = (e as Error).message || 'Failed to upload logo'
  } finally {
    isSavingLogo.value = false
  }
}

async function handleAddCarpet() {
  const id = 0
  const newCarpetIndex = eventCarpets.value.length + 1
  eventCarpets.value.push({
    id,
    eventId: props.eventData.id,
    name: `Carpet ${newCarpetIndex}`,
  })
}

async function handleRemoveCarpet(index: number) {
  eventCarpets.value = eventCarpets.value.filter((_, i) => i !== index)
}

async function handleUpdateCarpetName(index: number, name: string) {
  eventCarpets.value[index].name = name
}

async function updateCarpets() {
  if (isUpdatingCarpets.value) return
  isUpdatingCarpets.value = true

  try {
    await eventApi.updateEventCarpets(props.eventData.id, eventCarpets.value)
  } catch (e) {
    console.error('Error updating carpets:', e)
  } finally {
    isUpdatingCarpets.value = false
  }
}

const isInitializingScoreboards = ref(false)
async function initScoreboards() {
  if (isInitializingScoreboards.value) return
  isInitializingScoreboards.value = true

  try {
    await eventApi.initEventScoreboards(props.eventData.id)
  } catch (e) {
    console.error('Error initializing scoreboards:', e)
  } finally {
    isInitializingScoreboards.value = false
  }
}

async function saveEventData(onSuccess: () => void) {
  if (isPending.value || isUpdatingCarpets.value) return
  errorMessage.value = undefined

  try {
    // Update carpets if needed
    if (eventCarpets.value.length) {
      await updateCarpets()
      await initScoreboards()
    }

    const payload = {
      ...props.eventData,
      scoreboardBgImage: scoreboardBgImage.value,
      scoreboardLogo: scoreboardLogo.value,
      carpets: eventCarpets.value,
    }

    updateEvent(payload, { onSuccess })
  } catch (err) {
    errorMessage.value = (err as Error).message || 'Failed to save event data'
  }
}

function handleNext() {
  saveEventData(() => {
    emit('next')
  })
}

function handleCancel() {
  saveEventData(() => {
    emit('cancel')
  })
}

function openBgImageDialog() {
  inputBgImageRef.value?.click()
}

function handlerBgImageChange(evt: Event): void {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  saveBgImageToEvent(filesImg[0])
  const reader = new FileReader()
  reader.onload = (event) => {
    optimisticBgImage.value = event.target?.result as string
  }
  reader.readAsDataURL(filesImg[0])
}

function openLogoDialog() {
  inputLogoRef.value?.click()
}

function handlerLogoChange(evt: Event): void {
  const filesImg = (evt.target as HTMLInputElement).files
  if (!filesImg?.length) return

  saveLogoToEvent(filesImg[0])
  const reader = new FileReader()
  reader.onload = (event) => {
    optimisticLogo.value = event.target?.result as string
  }
  reader.readAsDataURL(filesImg[0])
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.display-mode {
  max-width: 400px;
  margin: 140px auto 60px;

  .dm-section {
    margin-bottom: 40px;

    &__title {
      margin: 12px 0;
      color: var(--color-dark-800);
      font-weight: var(--font-weight-semi-bold);
    }

    &__description {
      color: #999;
    }
  }

  .carpet-item {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
  }

  .carpet__add-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: var(--font-weight-bold);
  }

  .dm-logo {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    height: auto;
    margin: 0 auto;
    object-fit: cover;
    object-position: center;
    background: #6cace4;
    aspect-ratio: 1 / 1;
  }

  .dm-bg-image {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center;
    background: #6cace4;
    aspect-ratio: 16 / 9;
  }

  :deep(.v-responsive__content) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .btn-edit {
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-weight: var(--font-weight-bold);
  }

  .error--text {
    font-size: 14px;
    text-align: center;
    color: red;
  }
}
</style>
